// Byråns användare (ADR 0083). Alla ser vilka som finns. Ägaren bjuder in
// med e-post, ändrar roller och tar bort. Den inbjudna får ett mejl med ett
// tillfälligt lösenord och väljer sedan ett eget och registrerar MFA.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { ApiFel, data } from '../api/client.js';
import type { components } from '../api/schema.js';
import { useAuth, useMe } from '../auth/auth.js';
import { Sida } from '../layout/Sida.js';

type Anvandare = components['schemas']['FirmUser'];
type Roll = Anvandare['role'];

const ROLLER: Record<Roll, string> = { owner: 'Ägare', member: 'Medarbetare' };
const NYCKEL = ['anvandare'];

/** Problemets text från API:t, eller en allmän. */
function felText(err: unknown, annars: string): string {
  const detail = err instanceof ApiFel ? (err.problem as { detail?: unknown })?.detail : undefined;
  return typeof detail === 'string' ? detail : annars;
}

function Inbjudan() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const id = useId();
  const [namn, setNamn] = useState('');
  const [epost, setEpost] = useState('');
  const [roll, setRoll] = useState<Roll>('member');
  const [kvitto, setKvitto] = useState<string | null>(null);

  const bjud = useMutation({
    mutationFn: async () =>
      data(
        await api.POST('/v1/admin/users', {
          body: { name: namn.trim(), email: epost.trim(), role: roll },
        }),
      ),
    onSuccess: (ny) => {
      void queryClient.invalidateQueries({ queryKey: NYCKEL });
      setKvitto(`Inbjudan är skickad till ${ny.email}.`);
      setNamn('');
      setEpost('');
      setRoll('member');
    },
  });

  function skicka(e: FormEvent) {
    e.preventDefault();
    setKvitto(null);
    if (namn.trim() && epost.trim()) bjud.mutate();
  }

  return (
    <form className="kort panel-falt anvandare-inbjudan" onSubmit={skicka}>
      <h2 className="avsnitt-rubrik">Bjud in</h2>
      <p className="hjalp">
        Personen får ett mejl med ett tillfälligt lösenord, väljer ett eget och skyddar kontot med
        en kod från mobilen.
      </p>
      <div className="tre">
        <div className="falt">
          <label className="etikett" htmlFor={`${id}-namn`}>
            Namn
          </label>
          <input
            id={`${id}-namn`}
            className="in"
            required
            value={namn}
            onChange={(e) => setNamn(e.target.value)}
          />
        </div>
        <div className="falt">
          <label className="etikett" htmlFor={`${id}-epost`}>
            E-post
          </label>
          <input
            id={`${id}-epost`}
            className="in"
            type="email"
            required
            value={epost}
            onChange={(e) => setEpost(e.target.value)}
          />
        </div>
        <div className="falt">
          <label className="etikett" htmlFor={`${id}-roll`}>
            Roll
          </label>
          <select
            id={`${id}-roll`}
            className="in"
            value={roll}
            onChange={(e) => setRoll(e.target.value as Roll)}
          >
            <option value="member">Medarbetare</option>
            <option value="owner">Ägare</option>
          </select>
        </div>
      </div>
      {bjud.isError && (
        <div className="meddelande" role="alert">
          {felText(bjud.error, 'Inbjudan gick inte att skicka. Försök igen.')}
        </div>
      )}
      {kvitto && (
        <div className="kvitto" role="status">
          {kvitto}
        </div>
      )}
      <div>
        <button type="submit" className="btn btn-p" disabled={bjud.isPending}>
          {bjud.isPending ? 'Skickar …' : 'Skicka inbjudan'}
        </button>
      </div>
    </form>
  );
}

export function AnvandareSida() {
  const me = useMe();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const agare = me.user.role === 'owner';
  const [fel, setFel] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: NYCKEL,
    queryFn: async () => data(await api.GET('/v1/admin/users')).items,
  });

  const andraRoll = useMutation({
    mutationFn: async ({ anvandare, role }: { anvandare: Anvandare; role: Roll }) =>
      data(
        await api.PUT('/v1/admin/users/{id}/role', {
          params: { path: { id: anvandare.id } },
          body: { role },
        }),
      ),
    onMutate: () => setFel(null),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: NYCKEL }),
    onError: (err) => setFel(felText(err, 'Rollen gick inte att ändra.')),
  });

  const taBort = useMutation({
    mutationFn: async (anvandare: Anvandare) => {
      const svar = await api.DELETE('/v1/admin/users/{id}', {
        params: { path: { id: anvandare.id } },
      });
      if (!svar.response.ok) throw new ApiFel(svar.response.status, svar.error);
    },
    onMutate: () => setFel(null),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: NYCKEL }),
    onError: (err) => setFel(felText(err, 'Användaren gick inte att ta bort.')),
  });

  return (
    <Sida titel="Användare">
      <p className="ingress katalog-huvud">
        Alla loggar in med e-post, lösenord och en kod från mobilen. Ägare kan ändra allt.
        Medarbetare hanterar förfrågningar och offerter.
      </p>
      {!agare && (
        <div className="kort notis" role="note">
          Bara byråns ägare kan bjuda in och ta bort användare.
        </div>
      )}
      {fel && (
        <div className="meddelande" role="alert">
          {fel}
        </div>
      )}
      <div className="kort tabellkort">
        {lista.isError && (
          <p className="meddelande" role="alert">
            Användarna gick inte att hämta. Ladda om sidan.
          </p>
        )}
        {!lista.data && !lista.isError && <p role="status">Hämtar användarna …</p>}
        {lista.data && (
          // På mobilen rullar tabellen i sidled för sig, inte hela sidan.
          <div className="tabellrulle" tabIndex={0} role="region" aria-label="Byråns användare">
            <table className="tabell">
              <caption className="visuellt-dold">Byråns användare</caption>
              <thead>
                <tr>
                  <th scope="col">Namn och e-post</th>
                  <th scope="col">Roll</th>
                  {agare && (
                    <th scope="col" className="hoger">
                      <span className="visuellt-dold">Åtgärder</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {lista.data.map((a) => {
                  const sjalv = a.id === me.user.id;
                  return (
                    <tr key={a.id}>
                      <td>
                        {a.name}
                        {sjalv && <span className="hjalp"> (du)</span>}
                        <div className="hjalp">{a.email}</div>
                      </td>
                      <td>
                        {agare && !sjalv ? (
                          <select
                            className="in"
                            aria-label={`Roll för ${a.name}`}
                            value={a.role}
                            disabled={andraRoll.isPending}
                            onChange={(e) =>
                              andraRoll.mutate({ anvandare: a, role: e.target.value as Roll })
                            }
                          >
                            <option value="member">Medarbetare</option>
                            <option value="owner">Ägare</option>
                          </select>
                        ) : (
                          ROLLER[a.role]
                        )}
                      </td>
                      {agare && (
                        <td className="hoger">
                          {!sjalv && (
                            <button
                              type="button"
                              className="btn btn-s"
                              disabled={taBort.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Ta bort ${a.name}? Personen kan inte logga in längre.`,
                                  )
                                ) {
                                  taBort.mutate(a);
                                }
                              }}
                            >
                              Ta bort <span className="visuellt-dold">{a.name}</span>
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {agare && <Inbjudan />}
    </Sida>
  );
}
